/**
 * Issues API (org-scoped)
 *
 * Lets a customer read the OPEN operational issues affecting their own
 * organization (optionally narrowed to one entity, e.g. a deployment). The
 * frontend resolves user-facing presentation (title, guidance, CTA) from the
 * issue registry by `code`, so this payload stays minimal and leak-free —
 * notably it never returns the admin `message` or the raw error `rawPayload`.
 *
 * Base path: /api/issues
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const OrgIssueItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  category: z.string(),
  entityType: z.string(), // '' means no specific entity
  entityId: z.string(), // '' means no specific entity
  count: z.number().int(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string()
});

export const ListOrgIssuesResponseSchema = z.array(OrgIssueItemSchema);

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const ListOrgIssuesQuerySchema = z.object({
  entityType: z.string().optional(),
  entityId: z.string().optional()
});

// ═══════════════════════════════════════════════════════════════
// API SCHEMA OBJECT
// ═══════════════════════════════════════════════════════════════

export const ZIssues = {
  /**
   * List OPEN issues for the caller's organization.
   * GET /api/issues
   */
  List: {
    method: "GET" as const,
    path: "/api/issues",
    Params: ListOrgIssuesQuerySchema,
    Response: ApiSuccessSchema(ListOrgIssuesResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// EXPORTED TYPES
// ═══════════════════════════════════════════════════════════════

export type OrgIssueItem = z.infer<typeof OrgIssueItemSchema>;
export type ListOrgIssuesQuery = z.infer<typeof ListOrgIssuesQuerySchema>;
export type ListOrgIssuesResponse = z.infer<typeof ListOrgIssuesResponseSchema>;
